import { formatCascadeDate } from "./model";

export const CASCADE_TOOLTIP_COLORS = {
  background: "#1f2937",
  title: "#ffffff",
  body: "#e5e7eb",
} as const;

export interface ICascadeTooltipSample {
  pointName: string;
  measureName: string;
  date: string;
  value: number;
  unitCode: number;
  units: string;
}

export interface ICascadeTooltipLabels {
  point: string;
  measure: string;
  dateTime: string;
  value: string;
}

export const getCascadeTooltipLines = (
  sample: ICascadeTooltipSample,
  labels: ICascadeTooltipLabels,
  formatValue: (value: number, unitCode: number | null) => string,
): string[] => {
  const formattedDate = formatCascadeDate(sample.date);
  const fraction = formattedDate !== sample.date ? /\d{2}:\d{2}:\d{2}\.(\d+)/.exec(sample.date)?.[1] : null;
  const date = `${formattedDate}${fraction ? `.${fraction}` : ""}`;
  return [
    `${labels.point}: ${sample.pointName}`,
    `${labels.measure}: ${sample.measureName}`,
    `${labels.dateTime}: ${date}`,
    `${labels.value}: ${formatValue(sample.value, sample.unitCode)} ${sample.units}`.trim(),
  ];
};

export const getCascadeTooltipPosition = (
  point: { x: number; y: number },
  size: { width: number; height: number },
  bounds: { width: number; height: number },
  gap = 16,
): { x: number; y: number } => {
  const width = Math.max(0, size.width);
  const height = Math.max(0, size.height);
  const canvasWidth = Math.max(0, bounds.width);
  const canvasHeight = Math.max(0, bounds.height);
  const spacing = Math.max(0, gap);
  const clamp = (value: number, maximum: number) => Math.max(0, Math.min(value, Math.max(0, maximum)));
  const x = clamp(point.x, canvasWidth);
  const y = clamp(point.y, canvasHeight);
  const left = x - spacing;
  const right = canvasWidth - x - spacing;
  const above = y - spacing;
  const below = canvasHeight - y - spacing;
  let tooltipX = x - width / 2;
  let tooltipY = y - height / 2;

  if (right >= width || left >= width) {
    tooltipX = right >= width && right >= left ? x + spacing : x - spacing - width;
  } else if (below >= height || above >= height) {
    tooltipY = below >= height && below >= above ? y + spacing : y - spacing - height;
  } else if (canvasWidth - x >= width || x >= width) {
    const preferRight = canvasWidth - x >= width && canvasWidth - x >= x;
    const clearance = Math.min(spacing, (preferRight ? canvasWidth - x : x) - width);
    tooltipX = preferRight ? x + clearance : x - clearance - width;
  } else if (canvasHeight - y >= height || y >= height) {
    const preferBelow = canvasHeight - y >= height && canvasHeight - y >= y;
    const clearance = Math.min(spacing, (preferBelow ? canvasHeight - y : y) - height);
    tooltipY = preferBelow ? y + clearance : y - clearance - height;
  }

  return { x: clamp(tooltipX, canvasWidth - width), y: clamp(tooltipY, canvasHeight - height) };
};
